import {
  createManagementDashboardApi,
  type ManagementDashboardAssessmentStatsResponse,
  type ManagementDashboardCoursePerformanceCategory,
  type ManagementDashboardEngagementOverviewResponse,
  type ManagementDashboardLeaderboardPreviewItem,
} from '@e-learning/core';
import type {
  AdminCoursePerformanceRow,
  AdminEngagementOverviewPoint,
} from '@e-learning/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../../api/client';

const managementDashboardApi = createManagementDashboardApi(api);

interface EngagementOverviewMonthDto {
  referenceDateUtc?: string;
  totalActiveUsers?: number;
  monthlyActiveUsersWithInteraction?: number;
}

export function mapEngagementToChartPoints(
  r: ManagementDashboardEngagementOverviewResponse | null
): AdminEngagementOverviewPoint[] {
  const monthFormatter = new Intl.DateTimeFormat('pt-BR', {
    month: 'short',
    timeZone: 'UTC',
  });

  const normalizeMonthLabel = (value?: string) => {
    if (!value) return '';
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return '';
    const label = monthFormatter.format(parsedDate).replace('.', '');
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  if (!r) return [];
  if (Array.isArray(r.points) && r.points.length) {
    return r.points
      .map(p => ({
        label: p.monthLabel ?? '',
        totalUsers: p.totalUsers ?? 0,
        activeUsers: p.activeUsers ?? 0,
      }))
      .filter(p => p.label.length > 0);
  }

  const months = r.months;
  if (Array.isArray(months) && months.length) {
    if (typeof months[0] === 'object' && months[0] !== null) {
      const monthObjects = months as EngagementOverviewMonthDto[];
      return monthObjects
        .map(month => ({
          label: normalizeMonthLabel(month.referenceDateUtc),
          totalUsers: month.totalActiveUsers ?? 0,
          activeUsers: month.monthlyActiveUsersWithInteraction ?? 0,
        }))
        .filter(p => p.label.length > 0);
    }
  }

  const tu = r.totalUsersSeries;
  const au = r.activeUsersSeries;
  if (
    Array.isArray(months) &&
    months.length &&
    typeof months[0] === 'string' &&
    tu?.length &&
    au?.length
  ) {
    const monthLabels = months as string[];
    const n = Math.min(monthLabels.length, tu.length, au.length);
    return Array.from({ length: n }, (_, i) => ({
      label: monthLabels[i] ?? '',
      totalUsers: tu[i] ?? 0,
      activeUsers: au[i] ?? 0,
    })).filter(p => p.label.length > 0);
  }
  return [];
}

export function mapCourseCategoriesToRows(
  categories: ManagementDashboardCoursePerformanceCategory[]
): AdminCoursePerformanceRow[] {
  return categories.map(c => ({
    name: c.name ?? c.categoryName ?? '—',
    completion: Math.round(Number(c.completionPercent ?? 0)),
    inProgress: Math.round(Number(c.inProgressPercent ?? 0)),
  }));
}

export interface UseAdminDashboardManagementWidgetsResult {
  loading: boolean;
  assessmentStats: ManagementDashboardAssessmentStatsResponse | null;
  assessmentError: boolean;
  leaderboardItems: ManagementDashboardLeaderboardPreviewItem[];
  leaderboardError: boolean;
  engagementPoints: AdminEngagementOverviewPoint[];
  engagementError: boolean;
  courseRows: AdminCoursePerformanceRow[];
  courseError: boolean;
  refetch: () => void;
}

export function useAdminDashboardManagementWidgets(): UseAdminDashboardManagementWidgetsResult {
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const [assessmentStats, setAssessmentStats] =
    useState<ManagementDashboardAssessmentStatsResponse | null>(null);
  const [assessmentError, setAssessmentError] = useState(false);

  const [leaderboardItems, setLeaderboardItems] = useState<
    ManagementDashboardLeaderboardPreviewItem[]
  >([]);
  const [leaderboardError, setLeaderboardError] = useState(false);

  const [engagementRaw, setEngagementRaw] =
    useState<ManagementDashboardEngagementOverviewResponse | null>(null);
  const [engagementError, setEngagementError] = useState(false);

  const [courseRows, setCourseRows] = useState<AdminCoursePerformanceRow[]>(
    []
  );
  const [courseError, setCourseError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setAssessmentError(false);
    setLeaderboardError(false);
    setEngagementError(false);
    setCourseError(false);

    const run = async () => {
      const settled = await Promise.allSettled([
        managementDashboardApi.fetchAssessmentStats(),
        managementDashboardApi.fetchLeaderboardPreview(),
        managementDashboardApi.fetchEngagementOverview(),
        managementDashboardApi.fetchCoursePerformance(),
      ]);

      if (cancelled) return;

      const [a, l, e, c] = settled;

      if (a.status === 'fulfilled') {
        setAssessmentStats(a.value);
        setAssessmentError(false);
      } else {
        setAssessmentStats(null);
        setAssessmentError(true);
      }

      if (l.status === 'fulfilled') {
        setLeaderboardItems(l.value.items ?? []);
        setLeaderboardError(false);
      } else {
        setLeaderboardItems([]);
        setLeaderboardError(true);
      }

      if (e.status === 'fulfilled') {
        setEngagementRaw(e.value);
        setEngagementError(false);
      } else {
        setEngagementRaw(null);
        setEngagementError(true);
      }

      if (c.status === 'fulfilled') {
        setCourseRows(mapCourseCategoriesToRows(c.value.categories ?? []));
        setCourseError(false);
      } else {
        setCourseRows([]);
        setCourseError(true);
      }

      setLoading(false);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const engagementPoints = useMemo(
    () => mapEngagementToChartPoints(engagementRaw),
    [engagementRaw]
  );

  const refetch = useCallback(() => {
    setReloadToken(x => x + 1);
  }, []);

  return {
    loading,
    assessmentStats,
    assessmentError,
    leaderboardItems,
    leaderboardError,
    engagementPoints,
    engagementError,
    courseRows,
    courseError,
    refetch,
  };
}
